/**
 * Request-a-change modal for Settings > My-Kitchen read-only fields
 * (Plan 19). Mirrors the school-side `RequestChangeModal` from Plan 13
 * but targets the kitchen-org BlockingTask flow.
 *
 * CHEF_ADMIN clicks "Request a change" on a managed-by-Ordo field
 * (legal name, EIN, business address, served locations, payout
 * destination) → this Sheet opens pre-filled with the field + current
 * value. Submitting creates a Plan-27 task assigned to ORDO_ADMIN with
 * the urgency-derived SLA target.
 */

import { Button } from '@ordo/ui/button'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@ordo/ui/sheet'
import { toast } from '@ordo/ui/sonner'
import { Textarea } from '@ordo/ui/textarea'
import { useEffect, useState } from 'react'

import { type KitchenChangeRequestUrgency, useCreateKitchenChangeRequestMutation } from '@/api/kitchen-settings'

export interface KitchenChangeRequestModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  kitchenOrgId: string
  field: string
  /** Human-friendly field label (e.g. "Business legal name", "EIN"). */
  fieldLabel: string
  currentValue: string | null
}

const URGENCY_OPTIONS: Array<{ value: KitchenChangeRequestUrgency; label: string; description: string }> = [
  {
    value: 'ROUTINE',
    label: 'Routine',
    description: '5 business day SLA. Use for most field changes.',
  },
  {
    value: 'TIME_SENSITIVE',
    label: 'Time-sensitive',
    description: '1 business day SLA. Use when this blocks an upcoming menu publish or payout cycle.',
  },
  {
    value: 'URGENT',
    label: 'Urgent',
    description: 'Same-day SLA, escalates via SMS once Plan 36 dispatcher lands. Use sparingly.',
  },
]

export function KitchenChangeRequestModal({
  open,
  onOpenChange,
  kitchenOrgId,
  field,
  fieldLabel,
  currentValue,
}: KitchenChangeRequestModalProps) {
  const [proposedValue, setProposedValue] = useState('')
  const [reason, setReason] = useState('')
  const [urgency, setUrgency] = useState<KitchenChangeRequestUrgency>('ROUTINE')
  const mutation = useCreateKitchenChangeRequestMutation()

  // Reset whenever the modal opens — re-opening for a different field
  // shouldn't leak the prior payload in.
  useEffect(() => {
    if (open) {
      setProposedValue('')
      setReason('')
      setUrgency('ROUTINE')
    }
  }, [open, field])

  const canSubmit = proposedValue.trim().length > 0 && !mutation.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!canSubmit) {
      return
    }
    try {
      await mutation.mutateAsync({
        kitchenOrgId,
        field,
        currentValue,
        proposedValue: proposedValue.trim(),
        reason: reason.trim(),
        urgency,
      })
      toast.success('Change request submitted', {
        description: 'An Ordo admin will review and respond within the SLA window.',
      })
      onOpenChange(false)
    } catch (err) {
      toast.error('Could not submit change request', {
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" size="lg" className="flex flex-col gap-0 p-0">
        <SheetHeader className="shrink-0 border-b px-6 pb-4 pt-6">
          <SheetTitle>Request a change: {fieldLabel}</SheetTitle>
          <SheetDescription>
            Ordo-managed fields can't be edited directly — they affect AP payouts, 1099 reporting, or contractual
            relationships. Submit a request and an Ordo admin will review.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-0 overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5 text-sm">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Field</Label>
              <p className="font-medium">{fieldLabel}</p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Current value</Label>
              <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">{currentValue ?? '—'}</p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="kitchen-proposed-value">Proposed new value</Label>
              <Textarea
                id="kitchen-proposed-value"
                value={proposedValue}
                onChange={(e) => setProposedValue(e.target.value)}
                placeholder="What should it change to?"
                required
                rows={3}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="kitchen-change-reason">Reason / context (optional)</Label>
              <Textarea
                id="kitchen-change-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this changing? Any compliance, banking, or operational context the reviewer should know."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Urgency</Label>
              <RadioGroup
                value={urgency}
                onValueChange={(v) => setUrgency(v as KitchenChangeRequestUrgency)}
                className="flex flex-col gap-2">
                {URGENCY_OPTIONS.map((opt) => (
                  <Label
                    key={opt.value}
                    htmlFor={`kitchen-urgency-${opt.value}`}
                    className="flex cursor-pointer items-start gap-3 rounded-md border bg-card p-3 hover:bg-accent/50">
                    <RadioGroupItem id={`kitchen-urgency-${opt.value}`} value={opt.value} className="mt-0.5" />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-medium">{opt.label}</span>
                      <span className="text-xs text-muted-foreground">{opt.description}</span>
                    </div>
                  </Label>
                ))}
              </RadioGroup>
            </div>
          </div>

          <SheetFooter className="shrink-0 border-t px-6 py-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {mutation.isPending ? 'Submitting…' : 'Submit request'}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
